import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import {
  constructorStandingsQueryOptions,
  driverStandingsQueryOptions,
  seasonsQueryOptions,
} from "@/tools/f1-fantasy/hooks/use-f1-data";
import { DEFAULT_SEASON } from "@/tools/f1-fantasy/lib/constants";
import { Standings } from "@/tools/f1-fantasy/pages/Standings";

// Search params schema for tab state. `tab` is OPTIONAL with no default on
// purpose: a schema default is written back into the URL, which made the bare
// /f1-fantasy/standings 307 to ?tab=drivers — and since the canonical is the
// bare URL, Google saw a canonical pointing at a redirect ("Duplicate without
// user-selected canonical"). The page defaults to "drivers" itself.
const standingsSearchSchema = z.object({
  tab: z.enum(["drivers", "constructors"]).optional().catch(undefined),
});

export const Route = createFileRoute("/f1-fantasy/standings")({
  head: () => ({
    meta: [
      { title: "F1 Fantasy Standings | Toolverse" },
      {
        name: "description",
        content:
          "Driver and constructor fantasy standings for every F1 season. Track cumulative points, race wins, and podiums across every Grand Prix.",
      },
      {
        property: "og:title",
        content: "F1 Fantasy Standings | Toolverse",
      },
      {
        property: "og:description",
        content:
          "F1 Fantasy standings for drivers and constructors across all seasons, from 2023 onwards.",
      },
      ...i18nOgMeta("/f1-fantasy/standings"),
    ],
    links: [...i18nLinks("/f1-fantasy/standings")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: localeUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Fantasy",
              item: localeUrl("/f1-fantasy"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Standings",
              item: localeUrl("/f1-fantasy/standings"),
            },
          ],
        }),
      },
    ],
  }),
  component: Standings,
  validateSearch: standingsSearchSchema,
  // SSR streaming: await fast data, prefetch slow data (renders skeleton immediately)
  ssr: true,
  loader: async ({ context }) => {
    // Step 1: Await seasons (FAST: ~5ms) - needed for the season selector
    const seasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );

    // Get the latest season (first in the array since ordered DESC by year)
    const latestSeason = seasons?.[0]?.year ?? DEFAULT_SEASON;

    // Step 2: Ensure both standings are loaded before rendering to avoid hydration mismatch
    // (prefetchQuery dehydrates data to client, so client renders actual content during
    // hydration while server DOM has skeleton → mismatch; await both in parallel)
    await Promise.all([
      context.queryClient.ensureQueryData(
        driverStandingsQueryOptions(latestSeason),
      ),
      context.queryClient.ensureQueryData(
        constructorStandingsQueryOptions(latestSeason),
      ),
    ]);

    // Return the determined values so component can initialize with them
    return {
      season: latestSeason,
    };
  },
});
